/**
 * Service Appointment Status
 *
 * Lifecycle:
 * - pending: User submitted request, awaiting Zoho FSM processing
 * - scheduled: Service tech created appointment in Zoho (from webhook)
 * - confirmed: Appointment confirmed
 * - in_progress: Service work has started
 * - completed: Service work finished
 * - cancelled: Request/appointment cancelled
 *
 * Note: 'requested' is deprecated - use 'pending' for new records
 */
export type ServiceAppointmentStatus = 'pending' | 'requested' | 'scheduled' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';

/**
 * Service Appointment model
 * Represents a drone service appointment request.
 * Supports bi-directional sync with Zoho FSM.
 */
export interface ServiceAppointment {
  id: string;

  // Organization and drone relationships
  organizationId: string;
  droneId: string;

  // Drone details (populated on fetch)
  droneName?: string | null;
  droneSerialNumber?: string | null;

  // Zoho FSM integration
  zohoFsmRequestId?: string | null;      // Zoho Request ID (from initial sync)
  zohoFsmAppointmentId?: string | null;  // Actual Zoho Service Appointment ID (from webhook)
  zohoFsmWorkOrderId?: string | null;    // Zoho Work Order ID (from webhook)

  // Scheduling
  preferredDate: string;
  preferredTime: string;
  scheduledDate?: string | null;  // Actual scheduled date (set when tech creates appointment)

  // Appointment content
  issueDescription: string;
  status: ServiceAppointmentStatus;

  // Assignment (from Zoho - populated via webhook)
  technicianName?: string | null;

  // Notes
  notes?: string | null;

  // Cancellation
  cancelledAt?: string | null;
  cancelledReason?: string | null;

  // User who created the appointment
  createdByProfileId: string;
  createdByName?: string;

  // Audit fields
  createdAt: string;
  updatedAt: string;
}

/**
 * Attachment input for creating service appointments
 */
export interface ServiceAppointmentAttachmentInput {
  fileName: string;
  fileSize?: number;
  fileType?: string;
  fileExtension?: string;
  s3Key: string;
  s3Url: string;
  cdnUrl?: string;
}

/**
 * Service Appointment create payload
 */
export interface ServiceAppointmentCreate {
  organizationId: string;
  droneId: string;
  preferredDate: string;
  preferredTime: string;
  issueDescription: string;
  attachments?: ServiceAppointmentAttachmentInput[];
}

/**
 * Service Appointment list query parameters
 */
export interface ServiceAppointmentListQuery {
  status?: ServiceAppointmentStatus;
  page?: number;
  limit?: number;
}

/**
 * Service Appointment list response
 */
export interface ServiceAppointmentListResponse {
  appointments: ServiceAppointment[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Service Appointment status counts
 */
export interface ServiceAppointmentStatusCounts {
  pending: number;
  requested: number; // deprecated, kept for backwards compatibility
  scheduled: number;
  confirmed: number;
  in_progress: number;
  completed: number;
  cancelled: number;
}

/**
 * Status display info
 *
 * Colors:
 * - pending/requested: warning (yellow) - awaiting action
 * - scheduled: info (blue) - scheduled
 * - confirmed: primary (blue) - confirmed
 * - in_progress: info (blue) - active
 * - completed: success (green) - done
 * - cancelled: secondary (gray) - cancelled
 */
export const SERVICE_APPOINTMENT_STATUSES: { value: ServiceAppointmentStatus; label: string; color: string }[] = [
  { value: 'pending', label: 'Pending', color: 'warning' },
  { value: 'requested', label: 'Pending', color: 'warning' }, // deprecated, maps to same display as pending
  { value: 'scheduled', label: 'Scheduled', color: 'info' },
  { value: 'confirmed', label: 'Confirmed', color: 'primary' },
  { value: 'in_progress', label: 'In Progress', color: 'info' },
  { value: 'completed', label: 'Completed', color: 'success' },
  { value: 'cancelled', label: 'Cancelled', color: 'secondary' }
];

/**
 * Preferred time options for UI
 */
export const PREFERRED_TIME_OPTIONS: { value: string; label: string }[] = [
  { value: 'morning', label: 'Morning (8am-12pm)' },
  { value: 'afternoon', label: 'Afternoon (12pm-4pm)' },
  { value: 'evening', label: 'Evening (4pm-7pm)' },
  { value: 'anytime', label: 'Anytime' }
];
